import React, { useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import RowOptions from "../../Widgets/RowOptions";
import DataTable, { DataTableColumn, DataTableRow } from "../../Widgets/DataTable";
import FamilyMentalHealthHistoryModal, { FamilyMentalHealthHistoryFormValues } from "./FamilyMentalHealthHistoryModal";
import useGetFamilyMentalHealthHistory from "hooks/api/client/useGetFamilyMentalHealthHistory";
import useCreateFamilyMentalHealthHistory from "hooks/api/client/useCreateFamilyMentalHealthHistory";
import useUpdateFamilyMentalHealthHistory from "hooks/api/client/useUpdateFamilyMentalHealthHistory";
import useDeleteFamilyMentalHealthHistory from "hooks/api/client/useDeleteFamilyMentalHealthHistory";

const BASE_COLUMNS: DataTableColumn[] = [
  { key: "familyMemberName", label: "Family Member Name" },
  { key: "relationToChild", label: "Relation to Child" },
  { key: "diagnoses", label: "Diagnoses" },
  { key: "currentSupportServices", label: "Current Support Services" },
  { key: "pastSupportServices", label: "Past Support Services" },
];

const EDIT_COLUMN: DataTableColumn = { key: "actions", label: "" };

export const FamilyMentalHealthHistory: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { familyMentalHealthHistory, refetchFamilyMentalHealthHistory } = useGetFamilyMentalHealthHistory(clientIdNum);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const { createFamilyMentalHealthHistoryMutation, isCreateFamilyMentalHealthHistoryLoading } = useCreateFamilyMentalHealthHistory({
    onSuccess: () => {
      refetchFamilyMentalHealthHistory();
      closeModal();
    },
  });

  const { updateFamilyMentalHealthHistoryMutation, isUpdateFamilyMentalHealthHistoryLoading } = useUpdateFamilyMentalHealthHistory({
    onSuccess: () => {
      refetchFamilyMentalHealthHistory();
      closeModal();
    },
  });

  const { deleteFamilyMentalHealthHistoryMutation } = useDeleteFamilyMentalHealthHistory({
    onSuccess: () => {
      refetchFamilyMentalHealthHistory();
    },
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataTableRow | null>(null);

  const columns = [...BASE_COLUMNS, EDIT_COLUMN];

  const rows: DataTableRow[] = familyMentalHealthHistory.map((item: any) => ({
    id: String(item.id),
    familyMemberName: item.familyMember || "-",
    relationToChild: item.relationship || "-",
    diagnoses: item.diagnoses || "-",
    currentSupportServices: item.currentSupportServices || "-",
    pastSupportServices: item.pastSupportServices || "-",
  }));

  const handleSave = (values: FamilyMentalHealthHistoryFormValues) => {
    if (!clientIdNum) return;
    const payload = {
      familyMember: values.familyMember || null,
      relationship: values.relationship || null,
      diagnoses: values.diagnoses || null,
      currentSupportServices: values.currentSupportServices || null,
      pastSupportServices: values.pastSupportServices || null,
    };

    if (selectedRow) {
      updateFamilyMentalHealthHistoryMutation({ id: Number(selectedRow.id), payload });
    } else {
      createFamilyMentalHealthHistoryMutation({ clientId: clientIdNum, payload });
    }
  };

  return (
    <SectionWrapper
      title="Family Mental Health History"
      description="Past and current family mental health records."
      hideButtons
    >
      <DataTable
        columns={columns}
        rows={rows}
        ariaLabel="Family mental health history"
        emptyMessage="No family mental health history records"
        onAddNew={() => { setSelectedRow(null); setModalOpen(true); }}
        renderCell={(col, row) => {
          if (col.key === "actions") {
            return (
              <RowOptions
                onEdit={() => { setSelectedRow(row); setModalOpen(true); }}
                onDelete={() => deleteFamilyMentalHealthHistoryMutation(Number(row.id))}
              />
            );
          }
          return row[col.key] || "-";
        }}
      />

      <FamilyMentalHealthHistoryModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        isSaving={isCreateFamilyMentalHealthHistoryLoading || isUpdateFamilyMentalHealthHistoryLoading}
        initialValues={selectedRow ? {
          familyMember: selectedRow.familyMemberName !== "-" ? selectedRow.familyMemberName : "",
          relationship: selectedRow.relationToChild !== "-" ? selectedRow.relationToChild : "",
          diagnoses: selectedRow.diagnoses !== "-" ? selectedRow.diagnoses : "",
          currentSupportServices: selectedRow.currentSupportServices !== "-" ? selectedRow.currentSupportServices : "",
          pastSupportServices: selectedRow.pastSupportServices !== "-" ? selectedRow.pastSupportServices : "",
        } : undefined}
      />
    </SectionWrapper>
  );
};

export default FamilyMentalHealthHistory;
